import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { BusinessIcon, BuildingIcon, WaterDropIcon, CheckIcon } from '../components/Icons';
import './Products.css';

const shopNowStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '2px solid #0077b6',
  color: '#0077b6',
  background: '#ffffff',
  padding: '12px 18px',
  borderRadius: '8px',
  fontWeight: 600,
  textDecoration: 'none'
};

const singleImageBox = {
  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
  padding: '16px',
  borderRadius: '12px',
  border: '3px solid #48cae4',
  marginBottom: '16px',
  textAlign: 'center'
};

const Products = () => {
  return (
    <div className="products-page">
      <Helmet>
        <title>Water Systems & Products - RO Systems, Home RO, Ice Machines & Boreholes</title>
        <meta
          name="description"
          content="Explore our range of water systems including small shop installations, commercial plants, containerized systems, home RO, ice machines, and borehole packages."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="products-hero section small-hero">
        <div className="container">
          <div className="products-hero-content">
            <div className="products-text">
              <h1>Water Systems & Products</h1>
              <p className="lead">
                Commercial-grade RO plants, container systems, and home solutions—curated for every budget.
              </p>
              <div className="hero-cta">
                <Link to="/request" className="btn btn-primary">Get Help Choosing</Link>
                <Link to="#categories" className="btn btn-outline">Browse Categories</Link>
              </div>
            </div>
            <div className="products-visual" aria-hidden="true">
              <div className="products-icon">
                <WaterDropIcon size={56} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section id="categories" className="product-categories section">
        <div className="container">
          <div className="categories-grid">

            {/* Small Shop Installations (Most Popular) */}
            <div className="category-card featured">
              <div className="featured-badge">Most Popular</div>
              <div className="category-header">
                <div className="category-icon">
                  <BusinessIcon size={64} className="category-icon-svg" />
                </div>
                <h2>Small Shop Installations</h2>
                <p className="category-subtitle">Perfect for entrepreneurs starting their water business</p>
              </div>

              {/* Single image placeholder */}
              <div style={singleImageBox}>
                <h3 style={{ marginBottom: 10, color: '#023e8a' }}>Featured Install</h3>
                <div className="layout-item media-placeholder" style={{ padding: 0 }}>
                  <img src="/images/watershopS1.jpeg" alt="Water shop interior with counters and RO skids" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              </div>

              <div className="product-features">
                <h4>Included Components:</h4>
                <ul>
                  <li><CheckIcon size={16} className="check-icon" /> RO membrane filtration system</li>
                  <li><CheckIcon size={16} className="check-icon" /> UV sterilization unit</li>
                  <li><CheckIcon size={16} className="check-icon" /> Storage tanks (500L - 1000L)</li>
                  <li><CheckIcon size={16} className="check-icon" /> Bottle filling station</li>
                  <li><CheckIcon size={16} className="check-icon" /> Quality monitoring sensors</li>
                  <li><CheckIcon size={16} className="check-icon" /> Control panel & automation</li>
                </ul>
              </div>

              <div className="pricing-info">
                <div className="price-range">R5,000 - R300,000</div>
                <p className="price-note">Complete turnkey installation (packages scale by capacity)</p>
              </div>

              <div className="category-cta" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Link to="/request" className="btn btn-primary">Get Quote</Link>
                <a
                  href="https://watershopssa.co.za/pages/water-shop-installation"
                  target="_blank"
                  rel="noopener"
                  className="btn"
                  style={shopNowStyle}
                >
                  Shop Now
                </a>
              </div>
            </div>

            {/* Industrial & Commercial Plants */}
            <div className="category-card">
              <div className="category-header">
                <div className="category-icon">
                  <BuildingIcon size={64} className="category-icon-svg" />
                </div>
                <h2>Industrial & Commercial Plants</h2>
                <p className="category-subtitle">High-capacity systems for commercial operations</p>
              </div>

              {/* Single image placeholder */}
              <div style={singleImageBox}>
                <h3 style={{ marginBottom: 10, color: '#023e8a' }}>High-Capacity Plant</h3>
                <div className="layout-item media-placeholder" style={{ padding: 0 }}>
                  <img src="/images/CPPlant1.png" alt="Commercial purification plant layout" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              </div>

              <div className="product-features">
                <h4>Advanced Features:</h4>
                <ul>
                  <li><CheckIcon size={16} className="check-icon" /> Industrial RO membrane arrays</li>
                  <li><CheckIcon size={16} className="check-icon" /> Ultrafiltration pre-treatment</li>
                  <li><CheckIcon size={16} className="check-icon" /> Automated CIP (Clean-in-Place)</li>
                  <li><CheckIcon size={16} className="check-icon" /> SCADA monitoring system</li>
                  <li><CheckIcon size={16} className="check-icon" /> Remote diagnostics capability</li>
                  <li><CheckIcon size={16} className="check-icon" /> Backup power systems</li>
                </ul>
              </div>

              <div className="pricing-info">
                <div className="price-range">R500,000 - R2,500,000</div>
                <p className="price-note">Custom engineering & installation</p>
              </div>

              <div className="category-cta" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Link to="/request" className="btn btn-primary">Get Quote</Link>
                <a
                  href="https://watershopssa.co.za/pages/reverse-osmosis-systems"
                  target="_blank"
                  rel="noopener"
                  className="btn"
                  style={shopNowStyle}
                >
                  Shop Now
                </a>
              </div>
            </div>

            {/* Containerized Water Systems */}
            <div className="category-card">
              <div className="category-header">
                <div className="category-icon">
                  <BuildingIcon size={64} className="category-icon-svg" />
                </div>
                <h2>Containerized Water Systems</h2>
                <p className="category-subtitle">Mobile and modular water treatment solutions</p>
              </div>

              {/* Single image placeholder */}
              <div style={singleImageBox}>
                <h3 style={{ marginBottom: 10, color: '#023e8a' }}>Modular Build</h3>
                <div className="layout-item media-placeholder" style={{ padding: 0 }}>
                  <img src="/images/CPPlant4.png" alt="Containerized water system concept" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              </div>

              <div className="product-features">
                <h4>Key Benefits:</h4>
                <ul>
                  <li><CheckIcon size={16} className="check-icon" /> Quick deployment (2-3 days)</li>
                  <li><CheckIcon size={16} className="check-icon" /> Weather-resistant housing</li>
                  <li><CheckIcon size={16} className="check-icon" /> Minimal site preparation</li>
                  <li><CheckIcon size={16} className="check-icon" /> Scalable & relocatable</li>
                  <li><CheckIcon size={16} className="check-icon" /> Remote monitoring capable</li>
                  <li><CheckIcon size={16} className="check-icon" /> Emergency water supply ready</li>
                </ul>
              </div>

              <div className="pricing-info">
                <div className="price-range">R800,000 - R1,800,000</div>
                <p className="price-note">Includes container & installation</p>
              </div>

              <div className="category-cta" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Link to="/request" className="btn btn-primary">Get Quote</Link>
                <a
                  href="https://watershopssa.co.za/"
                  target="_blank"
                  rel="noopener"
                  className="btn"
                  style={shopNowStyle}
                >
                  Shop Now
                </a>
              </div>
            </div>

            {/* Home RO Machines */}
            <div className="category-card">
              <div className="category-header">
                <div className="category-icon">
                  <WaterDropIcon size={64} className="category-icon-svg" />
                </div>
                <h2>Home RO Machines</h2>
                <p className="category-subtitle">Compact under-sink and whole-home solutions</p>
              </div>

              {/* Single image placeholder */}
              <div style={singleImageBox}>
                <h3 style={{ marginBottom: 10, color: '#023e8a' }}>Home RO Install</h3>
                <div className="layout-item media-placeholder" style={{ padding: 0 }}>
                  <img src="/images/HRinstall1.jpeg" alt="Home RO installation example" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              </div>

              <div className="product-features">
                <h4>Highlights:</h4>
                <ul>
                  <li><CheckIcon size={16} className="check-icon" /> 5–6 stage RO (+ remineralization optional)</li>
                  <li><CheckIcon size={16} className="check-icon" /> 8–12 L pressure tank</li>
                  <li><CheckIcon size={16} className="check-icon" /> 190–400 GPD membranes</li>
                  <li><CheckIcon size={16} className="check-icon" /> Optional fridge/icemaker feed</li>
                </ul>
              </div>

              <div className="pricing-info">
                <div className="price-range">R5,000 - R25,000</div>
                <p className="price-note">Supply only or installed</p>
              </div>

              <div className="category-cta" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Link to="/request" className="btn btn-primary">Get Quote</Link>
                <a
                  href="https://watershopssa.co.za/pages/home-installations"
                  target="_blank"
                  rel="noopener"
                  className="btn"
                  style={shopNowStyle}
                >
                  Shop Now
                </a>
              </div>
            </div>

            {/* Ice Machines */}
            <div className="category-card">
              <div className="category-header">
                <div className="category-icon">
                  <BuildingIcon size={64} className="category-icon-svg" />
                </div>
                <h2>Ice Machines</h2>
                <p className="category-subtitle">Cube, flake & nugget—pair with RO for crystal-clear ice</p>
              </div>

              {/* Single image placeholder */}
              <div style={singleImageBox}>
                <h3 style={{ marginBottom: 10, color: '#023e8a' }}>Ice Production</h3>
                <div className="layout-item media-placeholder" style={{ padding: 0 }}>
                  <img src="/images/ice1.jpg" alt="Borehole treatment setup" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              </div>

              <div className="product-features">
                <h4>Specification Range:</h4>
                <ul>
                  <li><CheckIcon size={16} className="check-icon" /> 100 – 10,000 kg/day</li>
                  <li><CheckIcon size={16} className="check-icon" /> Cube / Flake / Nugget</li>
                  <li><CheckIcon size={16} className="check-icon" /> Storage bins & conveyors</li>
                  <li><CheckIcon size={16} className="check-icon" /> RO + filtration pretreatment kits</li>
                </ul>
              </div>

              <div className="pricing-info">
                <div className="price-range">R20,000 - R750,000</div>
                <p className="price-note">Machine-only or turnkey with storage</p>
              </div>

              <div className="category-cta" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Link to="/request" className="btn btn-primary">Get Quote</Link>
                <a
                  href="https://watershopssa.co.za/pages/ice-making-business"
                  target="_blank"
                  rel="noopener"
                  className="btn"
                  style={shopNowStyle}
                >
                  Shop Now
                </a>
              </div>
            </div>

            {/* Borehole Packages */}
            <div className="category-card">
              <div className="category-header">
                <div className="category-icon">
                  <BusinessIcon size={64} className="category-icon-svg" />
                </div>
                <h2>Borehole Packages</h2>
                <p className="category-subtitle">From source to taps—pump, treatment & storage</p>
              </div>

              {/* Single image placeholder */}
              <div style={singleImageBox}>
                <h3 style={{ marginBottom: 10, color: '#023e8a' }}>Borehole Package</h3>
                <div className="layout-item media-placeholder" style={{ padding: 0 }}>
                  <img src="/images/HRinstall4.jpeg" alt="Ice machine lineup" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              </div>

              <div className="product-features">
                <h4>Typical Package:</h4>
                <ul>
                  <li><CheckIcon size={16} className="check-icon" /> Submersible pump + VSD options</li>
                  <li><CheckIcon size={16} className="check-icon" /> Iron/Manganese/Hardness/Microbe treatment</li>
                  <li><CheckIcon size={16} className="check-icon" /> 2,500–10,000 L+ storage</li>
                  <li><CheckIcon size={16} className="check-icon" /> Full lab analysis</li>
                </ul>
              </div>

              <div className="pricing-info">
                <div className="price-range">R30,000 - R300,000</div>
                <p className="price-note">Excludes drilling; turnkey treatment & reticulation</p>
              </div>

              <div className="category-cta" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Link to="/request" className="btn btn-primary">Get Quote</Link>
                <a
                  href="https://watershopssa.co.za/search?q=borehole&options%5Bprefix%5D=last"
                  target="_blank"
                  rel="noopener"
                  className="btn"
                  style={shopNowStyle}
                >
                  Shop Now
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Product Comparison (Alkaline removed; Home RO added) */}
      <section className="product-comparison section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Compare Our Systems</h2>
            <p>Find the perfect water system for your business requirements</p>
          </div>

          <div className="comparison-table-wrapper">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th>Features</th>
                  <th>Small Shop</th>
                  <th>Commercial Plant</th>
                  <th>Containerized</th>
                  <th>Home RO</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="feature-name">Daily Capacity</td>
                  <td>500–2,000 L</td>
                  <td>5,000–50,000 L+</td>
                  <td>2,000–20,000 L</td>
                  <td>200–600 L</td>
                </tr>
                <tr>
                  <td className="feature-name">Investment Range</td>
                  <td>R5k–300k</td>
                  <td>R500k–2.5M</td>
                  <td>R800k–1.8M</td>
                  <td>R5k–25k</td>
                </tr>
                <tr>
                  <td className="feature-name">Setup Time</td>
                  <td>1–2 weeks</td>
                  <td>4–8 weeks</td>
                  <td>2–3 days</td>
                  <td>1–3 days</td>
                </tr>
                <tr>
                  <td className="feature-name">Target Market</td>
                  <td>Local retail</td>
                  <td>B2B/Wholesale</td>
                  <td>Mobile/Emergency</td>
                  <td>Households</td>
                </tr>
                <tr>
                  <td className="feature-name">ROI Period</td>
                  <td>6–12 months</td>
                  <td>12–18 months</td>
                  <td>8–15 months</td>
                  <td>6–10 months</td>
                </tr>
                <tr>
                  <td className="feature-name">Maintenance</td>
                  <td>Low</td>
                  <td>Medium</td>
                  <td>Low–Medium</td>
                  <td>Low</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="tech-specs section">
        <div className="container">
          <div className="section-header text-center">
            <h2>Technical Excellence</h2>
            <p>Industry-leading technology and components in every system</p>
          </div>

          <div className="tech-grid">
            <div className="tech-item">
              <div className="tech-icon">
                <CheckIcon size={48} className="tech-icon-svg" />
              </div>
              <h3>RO Membrane Technology</h3>
              <p>High-efficiency reverse osmosis membranes removing 99.9% of contaminants including bacteria, viruses, heavy metals, and dissolved solids.</p>
              <ul>
                <li>TFC (Thin Film Composite) membranes</li>
                <li>Low energy consumption design</li>
                <li>Extended lifespan (2-3 years)</li>
                <li>NSF/ANSI certified components</li>
              </ul>
            </div>

            <div className="tech-item">
              <div className="tech-icon">
                <CheckIcon size={48} className="tech-icon-svg" />
              </div>
              <h3>Automation & Control</h3>
              <p>Smart control systems with IoT connectivity for optimal performance monitoring and predictive maintenance scheduling.</p>
              <ul>
                <li>PLC-based control systems</li>
                <li>Real-time water quality monitoring</li>
                <li>Remote diagnostic capabilities</li>
                <li>Mobile app integration</li>
              </ul>
            </div>

            <div className="tech-item">
              <div className="tech-icon">
                <CheckIcon size={48} className="tech-icon-svg" />
              </div>
              <h3>Safety & Compliance</h3>
              <p>All systems designed to exceed South African water quality standards with multiple safety redundancies and compliance features.</p>
              <ul>
                <li>SANS 241 compliance certified</li>
                <li>Multi-barrier treatment approach</li>
                <li>Automated shutdown protection</li>
                <li>Quality assurance protocols</li>
              </ul>
            </div>

            <div className="tech-item">
              <div className="tech-icon">
                <CheckIcon size={48} className="tech-icon-svg" />
              </div>
              <h3>Environmental Efficiency</h3>
              <p>Eco-friendly designs minimizing water waste and energy consumption while maximizing production efficiency and sustainability.</p>
              <ul>
                <li>High recovery rate (75-80%)</li>
                <li>Energy-efficient pumps</li>
                <li>Waste water recycling options</li>
                <li>Solar power integration ready</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Customization Options */}
      <section className="customization section">
        <div className="container">
          <div className="customization-content">
            <div className="custom-text">
              <h2>Custom Solutions for Unique Needs</h2>
              <p>Every business is different. We design and build custom water treatment solutions tailored to your specific requirements, location, and budget.</p>

              <div className="custom-options">
                <h4>Customization Options:</h4>
                <div className="options-grid">
                  <div className="option-item">
                    <h5>Branding & Design</h5>
                    <p>Custom colors, logos, and branding integration</p>
                  </div>
                  <div className="option-item">
                    <h5>Power Solutions</h5>
                    <p>Solar, generator, or hybrid power systems</p>
                  </div>
                  <div className="option-item">
                    <h5>Monitoring Systems</h5>
                    <p>Advanced analytics and reporting dashboards</p>
                  </div>
                  <div className="option-item">
                    <h5>Packaging Lines</h5>
                    <p>Bottle sizes, labeling, and packaging automation</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="custom-visual">
              <div className="custom-box">
                <h3>Get Custom Quote</h3>
                <p>Tell us about your specific needs and we'll design the perfect solution for you.</p>
                <div className="custom-form">
                  <input type="text" placeholder="Business Type" />
                  <input type="text" placeholder="Location" />
                  <input type="text" placeholder="Daily Volume Needed" />
                  <select>
                    <option>Budget Range</option>
                    <option>Under R200,000</option>
                    <option>R200,000 - R500,000</option>
                    <option>R500,000 - R1,000,000</option>
                    <option>Over R1,000,000</option>
                  </select>
                  <Link to="/request" className="btn btn-primary btn-full">Request Custom Design</Link>
                </div>
                <div style={{ marginTop: 12, textAlign: 'center' }}>
                  <a
                    href="https://your-shopify-store.com/collections"
                    target="_blank"
                    rel="noopener"
                    className="btn"
                    style={shopNowStyle}
                  >
                    Browse Products
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="products-cta section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Choose Your Water System?</h2>
            <p>Get expert guidance on selecting the perfect system for your business goals and budget</p>
            <div className="cta-buttons">
              <Link to="/request" className="btn btn-primary">Get Detailed Quote</Link>
              <Link to="/contact" className="btn btn-outline">Speak to Expert</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
